/**
 * FileUploadZone — Drag-and-drop file upload with:
 *   - Dashed border zone that highlights on drag-over
 *   - Immediate file icon + filename on drop/select
 *   - Progress indicator while uploading
 *   - Checkmark confirmation once upload completes
 *   - Remove/replace button on uploaded file
 */
import { useState, useCallback, useRef } from "react";
import { Upload, FileText, Loader2, Check, X } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

export default function FileUploadZone({
  onFileSelect,
  file = null,
  accept = ".pdf,.doc,.docx,.png,.jpg,.jpeg",
  label = "Upload file",
  hint = "PDF, DOC, or image files",
  uploading = false,
  uploadComplete = false,
  onRemove,
  className = "",
}) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      onFileSelect(droppedFile);
    }
  }, [onFileSelect]);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
    // Reset input so the same file can be re-selected
    if (inputRef.current) inputRef.current.value = "";
  }, [onFileSelect]);

  const handleRemove = useCallback((e) => {
    e.stopPropagation();
    if (onRemove) onRemove();
  }, [onRemove]);

  // File is selected (either pending upload, uploading, or complete)
  if (file) {
    return (
      <div className={cn("mt-2", className)}>
        <div className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-md border-2 transition-all",
          uploadComplete
            ? "border-green-300 bg-status-green-dim"
            : uploading
              ? "border-blue-300 bg-blue-50"
              : "border-gray-300 bg-gray-50"
        )}>
          {/* Icon */}
          <div className={cn(
            "w-10 h-10 rounded-md flex items-center justify-center shrink-0",
            uploadComplete ? "bg-status-green-dim" : uploading ? "bg-blue-100" : "bg-gray-100"
          )}>
            {uploadComplete ? (
              <Check className="w-5 h-5 text-status-green" />
            ) : uploading ? (
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            ) : (
              <FileText className="w-5 h-5 text-gray-500" />
            )}
          </div>

          {/* File info */}
          <div className="flex-1 min-w-0">
            <p className={cn(
              "text-sm font-medium truncate",
              uploadComplete ? "text-status-green" : "text-gray-700"
            )}>
              {file.name}
            </p>
            <p className="text-xs text-gray-500">
              {uploadComplete
                ? "Upload complete"
                : uploading
                  ? "Uploading..."
                  : `${(file.size / 1024).toFixed(0)} KB — Ready to upload`}
            </p>
            {/* Progress bar during upload */}
            {uploading && (
              <div className="mt-1.5 w-full bg-blue-100 rounded-full h-1.5">
                <div className="bg-blue-500 h-1.5 rounded-full animate-pulse" style={{ width: "60%" }} />
              </div>
            )}
          </div>

          {/* Remove / replace button */}
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={handleRemove}
            className="text-gray-400 hover:text-gray-600 p-1 h-auto shrink-0"
            disabled={uploading}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Empty state — drop zone
  return (
    <div className={cn("mt-2", className)}>
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-md cursor-pointer transition-all",
          dragOver
            ? "border-teal bg-teal/5 scale-[1.01]"
            : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
        )}
      >
        <Upload className={cn(
          "w-8 h-8 mb-2 transition-colors",
          dragOver ? "text-teal" : "text-gray-400"
        )} />
        <p className={cn(
          "text-sm font-medium",
          dragOver ? "text-teal" : "text-gray-500"
        )}>
          {dragOver ? "Drop file here" : label}
        </p>
        <p className="text-xs text-gray-400 mt-1">{hint}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleInputChange}
        accept={accept}
      />
    </div>
  );
}
